/**
 * EmailList — renders the email list pane from JSON signals.
 * Uses IntersectionObserver at the bottom sentinel to auto-load next page.
 */

import { h, Fragment } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { resolveFolderLabel } from '@tshmanager/email-shared'
import { EmailListRow } from './EmailListRow.jsx'
import { EmailSearch } from './EmailSearch.jsx'
import { EmailBulkToolbar } from './EmailBulkToolbar.jsx'
const FOLDER_ICONS = {
  inbox:    'bi-inbox',
  starred:  'bi-star',
  sent:     'bi-send',
  drafts:   'bi-file-earmark',
  archived: 'bi-archive',
  trash:    'bi-trash',
}

export function EmailList({ store, i18n }) {
  const sentinelRef = useRef(null)
  const observerRef = useRef(null)

  const emails        = store.emails.value
  const meta          = store.emailsMeta.value
  const loading       = store.emailsLoading.value
  const loadingMore   = store.emailsLoadingMore.value
  const hasSelection  = store.hasSelection.value
  const currentThreadId = store.currentThreadId.value
  const activeMailbox = store.activeMailbox.value
  const activeFolder  = store.activeFolder.value
  const mailboxes     = store.mailboxes.value
  const isMobile      = store.isMobileLayout.value

  // Resolve display name for active folder
  const folderKey = Object.keys(FOLDER_ICONS).find(k => k === activeFolder)
  const folderIcon = folderKey ? FOLDER_ICONS[folderKey] : 'bi-folder'
  const folderLabel = folderKey
    ? i18n?.t(`emails.folders.${folderKey}`)
    : resolveFolderLabel(activeFolder, {
        emailFolders: store.emailFolders.value,
        emailTags:    store.emailTags.value,
      }) ?? activeFolder

  // Active mailbox display
  const activeMailboxInfo = activeMailbox !== null
    ? mailboxes.find(m => m.id === activeMailbox)
    : null

  // Set up IntersectionObserver on the sentinel element for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && meta.next_page && !loadingMore) {
          store.fetchMoreEmails()
        }
      },
      { rootMargin: '200px' }
    )
    observerRef.current.observe(sentinelRef.current)

    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [sentinelRef.current, meta.next_page])

  return (
    <div class="email-list-pane-inner d-flex flex-column h-100">

      {/* Search & filter bar */}
      <EmailSearch store={store} i18n={i18n} isMobile={isMobile} />

      {/* Context bar: active mailbox + folder label */}
      <div class="email-list-context-bar d-flex align-items-center gap-1 px-2 py-1 border-bottom">
        <i class={`bi ${folderIcon} text-muted flex-shrink-0`} style="font-size:0.8rem" />
        <span class="text-truncate flex-grow-1" style="font-size:0.8rem;color:var(--bs-secondary-color);font-weight:500">
          {activeMailboxInfo
            ? `${activeMailboxInfo.name} — ${folderLabel}`
            : `${i18n?.t('emails.all_mailboxes')} — ${folderLabel}`
          }
        </span>
      </div>

      {/* Bulk toolbar (replaces filter bar when rows are selected) */}
      {hasSelection && <EmailBulkToolbar store={store} i18n={i18n} />}

      {/* List header: unread + total counts */}
      {!hasSelection && (
        <div class="email-list-header d-flex align-items-center gap-2 px-2 py-1 border-bottom">
          <span class="small text-muted">
            {meta.unread_count > 0 && (
              <>
                <span class="badge bg-primary-subtle text-primary me-1">
                  {meta.unread_count}
                </span>
                <span class="me-2" style="font-size:0.7rem">{i18n?.t('emails.unread_count')}</span>
              </>
            )}
            <span>{meta.total_count}</span>
            <span class="ms-1" style="font-size:0.7rem">{i18n?.t('emails.total_count')}</span>
          </span>
          <div class="ms-auto d-flex gap-1">
            <button
              class="btn btn-sm btn-link p-0 text-muted"
              title={i18n?.t('emails.select_all')}
              onClick={() => store.selectAll(emails.map(e => e.id))}
            >
              <i class="bi bi-check-all" />
            </button>
          </div>
        </div>
      )}

      {/* Email rows */}
      <div class="email-list-scroll flex-grow-1 overflow-auto" id="email_list_items">
        {loading ? (
          <div class="d-flex align-items-center justify-content-center py-5 text-muted">
            <div class="spinner-border spinner-border-sm me-2" role="status" />
            <span>{i18n?.t('helpers.loading')}</span>
          </div>
        ) : emails.length === 0 ? (
          <div class="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
            <i class="bi bi-inbox mb-2" style="font-size:2rem;opacity:0.4" />
            <span class="small">{i18n?.t('emails.empty_folder')}</span>
          </div>
        ) : (
          <>
            {emails.map(email => (
              <EmailListRow
                key={email.id}
                email={email}
                store={store}
                isActive={email.id === currentThreadId}
                i18n={i18n}
              />
            ))}

            {/* Infinite scroll sentinel */}
            {meta.next_page && (
              <div ref={sentinelRef} id="email_pagination" class="email-list-sentinel py-2 text-center text-muted">
                {loadingMore && (
                  <div class="spinner-border spinner-border-sm" role="status" />
                )}
              </div>
            )}
          </>
        )}
      </div>

    </div>
  )
}
